import React from 'react'
import { Controller, type Control, type Path, type FieldValues } from "react-hook-form"
import type { BaseTextFieldProps } from "@mui/material/TextField"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/cs'
import dayjs, { type Dayjs } from "dayjs"
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import type { PickersShortcutsItem } from "@mui/x-date-pickers"

interface RHFAutocompleteProps<
    TField extends FieldValues,
> {
    control: Control<TField>,
    name: Path<TField>,
    label?: string,
    size?: BaseTextFieldProps["size"],
    required?: BaseTextFieldProps["required"],
    autoFocus?: BaseTextFieldProps["autoFocus"],
    showShortcuts?: boolean,
}

const shortcutsItems: PickersShortcutsItem<Dayjs | null>[] = [
    {
        label: 'Začátek roku',
        getValue: () => {
            return dayjs().startOf('year')
        },
    },
    {
        label: 'Konec roku',
        getValue: () => {
            return dayjs().endOf('year')
        },
    },
    {
        label: 'Začátek měsíce',
        getValue: () => {
            return dayjs().startOf('month')
        },
    },
    {
        label: 'Konec měsíce',
        getValue: () => {
            return dayjs().endOf('month')
        },
    },
    {
        label: 'Dnes',
        getValue: () => {
            return dayjs()
        },
    },
    {
        label: 'Zítra',
        getValue: () => {
            return dayjs().add(1, 'day')
        },
    },
]

const RHFDatePicker = <
    TField extends FieldValues,
>(
    props: RHFAutocompleteProps<TField>
) => {

    const {
        control,
        name,
        label,
        size,
        required,
        autoFocus,
        showShortcuts,
    } = props

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
            <Controller
                name={name}
                control={control}
                render={({ field }) => {
                    return (
                        <DatePicker
                            label={label}
                            views={['year', 'month', 'day']}
                            {...field}
                            value={field.value ? dayjs(field.value) : null}
                            onChange={newDate => {
                                field.onChange(newDate?.format('YYYY-MM-DD'))
                            }}
                            slotProps={{
                                textField: { size, required, autoFocus, },
                                shortcuts: showShortcuts ? {
                                    items: shortcutsItems,
                                    changeImportance: 'set',
                                } : undefined,
                            }}
                        />
                    )
                }}
            />
        </LocalizationProvider>
    )
}

export default RHFDatePicker
